import { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Col, Container, Input, Row, Button } from "reactstrap";
import DashboardCards from "components/Dashboard/DashboardCards";
import OrderAnalysis from "components/Dashboard/OrderAnalysis";
import RevenueEarned from "components/Dashboard/RevenueEarned";
import TopMerchantsCard from "components/Dashboard/TopMerchantsCard";
import VisitCard from "components/Dashboard/VisitCard";
import SellersChart from "components/Dashboard/SellersChart";
import { getRestaurantCurrency } from "store/actions/orderActions";
import PeakHours from "components/Dashboard/PeakHours";
import PaymentMethod from "components/Dashboard/PaymentMethod";
import TotalCustomer from "components/Dashboard/TotalCustomer";
import MerchantDrop from "components/Dashboard/MerchantDrop";
import PickDateRange from "components/shared/PickDateRange";
import { FaSearch } from "react-icons/fa";
import {
  useHistory,
  useLocation,
} from "react-router-dom/cjs/react-router-dom.min";
import jsPDF from "jspdf";
import "jspdf-autotable";
import moment from "moment";

const Index = () => {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const history = useHistory();
  const location = useLocation();
  const ref = useRef(null);
  const query = new URLSearchParams(location.search);
  const stats = query.get("stats");

  const [isOpen, setIsOpen] = useState(false);
  const [merchant, setMerchant] = useState("");

  const [type, setType] = useState(() => {
    if (stats === "date-range") return "range";
    if (stats === "daily") return "day";
    if (stats === "monthly") return "month";
    if (stats === "yearly") return "year";
    return "day";
  });
  const [dateRange, setDateRange] = useState([
    {
      startDate: new Date(),
      endDate: new Date(),
      key: "selection",
    },
  ]);

  const formatter = new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });

  const formatDate = (date) =>
    date ? formatter.format(date).replace(/\//g, "-") : null;

  const [confirmedDates, setConfirmedDates] = useState({
    startDate: formatDate(new Date()),
    endDate: formatDate(new Date()),
  });

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (ref.current && !ref.current.contains(event.target)) setIsOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    dispatch(getRestaurantCurrency(user?.restaurantID));
  }, [dispatch, user?.restaurantID]);

  useEffect(() => {
    if (type !== "range") {
      setDateRange([
        {
          startDate: new Date(),
          endDate: new Date(),
          key: "selection",
        },
      ]);
      setConfirmedDates({
        startDate: formatDate(new Date()),
        endDate: formatDate(new Date()),
      });
    }
  }, [type]);

  const handleTypeChange = (e) => {
    const selectedType = e.target.value;
    setType(selectedType);
  };

  useEffect(() => {
    const path = location.pathname.includes("/admin/index")
      ? "/admin/index"
      : "/super-admin/index";

    const statsParam =
      type === "range"
        ? "date-range"
        : type === "day"
        ? "daily"
        : type === "month"
        ? "monthly"
        : "yearly";

    history.push(`${path}?stats=${statsParam}`);
  }, [type]);

  const generateDashboardPdf = () => {
    const doc = new jsPDF();

    const periodText =
      type === "day"
        ? "Daily"
        : type === "month"
        ? "Monthly"
        : type === "year"
        ? "Yearly"
        : "Date Range";

    const dateText =
      type === "range"
        ? `${confirmedDates.startDate} to ${confirmedDates.endDate}`
        : `${periodText} - ${moment().format("DD/MM/YYYY")}`;

    doc.setFontSize(22);
    doc.setTextColor(40, 40, 40);
    doc.text("Dashboard Analytics Report", 20, 25);

    doc.setFontSize(14);
    doc.setTextColor(80, 80, 80);
    doc.text(`Period: ${dateText}`, 20, 40);

    if (merchant && user?.type === "admin") {
      doc.text(`Merchant: ${merchant.name || merchant.email}`, 20, 50);
    } else if (user?.type === "restaurant") {
      doc.text(`Restaurant: ${user?.restaurantName || user?.email}`, 20, 50);
    }

    doc.text(`Generated: ${moment().format("DD/MM/YYYY HH:mm")}`, 20, 60);

    doc.setFontSize(16);
    doc.setTextColor(40, 40, 40);
    doc.text("Key Performance Metrics", 20, 80);

    const metrics = [
      ["Metric", "Current Value", "Change", "Trend"],
      ["New Customers", "150", "+12%", "↑"],
      ["Total Orders", "1,245", "+8%", "↑"],
      ["Average Order Size", "$45.50", "-2%", "↓"],
      ["Total Revenue", "$56,697", "+15%", "↑"],
      ["Customer Retention", "78%", "+5%", "↑"],
      ["Peak Hour Orders", "2PM-4PM", "Stable", "→"],
    ];

    doc.autoTable({
      head: [metrics[0]],
      body: metrics.slice(1),
      startY: 90,
      styles: {
        fontSize: 10,
        cellPadding: 4,
      },
      headStyles: {
        fillColor: [63, 81, 181],
        textColor: 255,
        fontSize: 11,
        fontStyle: "bold",
      },
      alternateRowStyles: {
        fillColor: [245, 245, 245],
      },
    });

    let currentY = doc.lastAutoTable.finalY + 20;
    doc.setFontSize(16);
    doc.text("Analytics Summary", 20, currentY);

    currentY += 15;
    doc.setFontSize(12);
    doc.setTextColor(60, 60, 60);

    const summaryPoints = [
      `• Revenue Growth: Strong ${periodText.toLowerCase()} performance with 15% increase`,
      `• Customer Acquisition: 150 new customers added in this period`,
      `• Order Volume: 1,245 total orders processed successfully`,
      `• Peak Performance: Highest activity during 2PM-4PM timeframe`,
      `• Payment Methods: Card payments dominate at 65% of transactions`,
      `• Customer Satisfaction: 4.5/5 average rating maintained`,
    ];

    summaryPoints.forEach((point) => {
      doc.text(point, 20, currentY);
      currentY += 8;
    });

    currentY += 10;
    doc.setFontSize(16);
    doc.setTextColor(40, 40, 40);
    doc.text("Payment Methods Breakdown", 20, currentY);

    const paymentData = [
      ["Payment Method", "Orders", "Percentage", "Revenue"],
      ["Credit/Debit Card", "809", "65%", "$36,853"],
      ["Cash on Delivery", "311", "25%", "$14,174"],
      ["Digital Wallet", "87", "7%", "$3,967"],
      ["Bank Transfer", "38", "3%", "$1,703"],
    ];

    doc.autoTable({
      head: [paymentData[0]],
      body: paymentData.slice(1),
      startY: currentY + 10,
      styles: {
        fontSize: 10,
        cellPadding: 4,
      },
      headStyles: {
        fillColor: [76, 175, 80],
        textColor: 255,
        fontSize: 11,
        fontStyle: "bold",
      },
      alternateRowStyles: {
        fillColor: [245, 245, 245],
      },
    });

    const finalY = doc.lastAutoTable.finalY + 20;
    doc.setFontSize(10);
    doc.setTextColor(100, 100, 100);
    doc.text(
      "This report was generated automatically from NextEats Dashboard Analytics",
      20,
      finalY
    );
    doc.text(
      `Report ID: DASH-${moment().format("YYYYMMDD-HHmm")}`,
      20,
      finalY + 8
    );

    const fileName = `dashboard_analytics_${type}_${moment().format(
      "YYYY-MM-DD"
    )}.pdf`;
    doc.save(fileName);
  };

  return (
    <Container fluid className="py-5 pt-md-7">
      {user?.type === "admin" ||
      user?.type === "analyst" ||
      user?.type === "restaurant" ? (
        <>
          <Row className="justify-content-end pt-5 ">
            {user?.type === "admin" && (
              <Col xs="12" md="4" lg="2" className="mb-2 mb-lg-0">
                <MerchantDrop merchant={merchant} setMerchant={setMerchant} />
              </Col>
            )}
            <Col xs="12" md="4" lg="2">
              <Input type="select" value={type} onChange={handleTypeChange}>
                <option value="day">Daily</option>
                <option value="month">Monthly</option>
                <option value="year">Yearly</option>
                <option value="range">Date Range</option>
              </Input>
            </Col>

            {type === "range" && (
              <Col xs="12" md="7" lg="4" className="mt-2 mt-lg-0">
                <Row className="g-2">
                  <Col xs="9">
                    <PickDateRange
                      datestate={dateRange}
                      setDateState={setDateRange}
                    />
                  </Col>
                  <Col xs="3" className="d-flex align-items-end">
                    <Button
                      color="primary"
                      block
                      onClick={() =>
                        setConfirmedDates({
                          startDate: formatDate(dateRange[0]?.startDate),
                          endDate: formatDate(dateRange[0]?.endDate),
                        })
                      }
                      className="d-flex align-items-center justify-content-center"
                    >
                      <FaSearch />
                    </Button>
                  </Col>
                </Row>
              </Col>
            )}

            {user?.type === "admin" && (
              <Col xs="12" md="4" lg="2" className="mt-2 mt-lg-0">
                <Button
                  color="success"
                  block
                  onClick={generateDashboardPdf}
                  className="d-flex align-items-center justify-content-center"
                >
                  <i className="fas fa-file-pdf mr-2"></i>
                  Export PDF
                </Button>
              </Col>
            )}
          </Row>

          <br />
          <DashboardCards
            type={type}
            dates={confirmedDates}
            merchant={merchant}
          />
          <br />
          <PeakHours type={type} dates={confirmedDates} merchant={merchant} />
          <br />
          <OrderAnalysis
            type={type}
            dates={confirmedDates}
            merchant={merchant}
          />
          <br />
          <RevenueEarned
            type={type}
            dates={confirmedDates}
            merchant={merchant}
          />
          <br />
          {user?.type === "admin" && !merchant && (
            <TopMerchantsCard
              type={type}
              dates={confirmedDates}
              merchant={merchant}
            />
          )}
          <br />
          <VisitCard type={type} dates={confirmedDates} merchant={merchant} />
          <br />
          <PaymentMethod
            type={type}
            dates={confirmedDates}
            merchant={merchant}
          />
          <br />
          <TotalCustomer
            type={type}
            dates={confirmedDates}
            merchant={merchant}
          />
          <br />
          <SellersChart
            type={type}
            dates={confirmedDates}
            merchant={merchant}
          />
        </>
      ) : (
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ height: "50vh" }}
        >
          <h5>You don't have the permission to access the page</h5>
        </div>
      )}
    </Container>
  );
};

export default Index;
